// @ts-check
import { useState, useCallback } from 'react';
import { licenseService } from '../../../apiService';

/**
 * @typedef {Object} License
 * @property {string} serialNumber
 * @property {string} [deviceId]
 * @property {boolean} [active]
 * @property {boolean} [deleted]
 * @property {string} [expiresAt]
 */

/**
 * Owns the license list, pagination cursor, and the loading/error flags for the
 * table. Extracted verbatim from the original LicenseTable so behavior is
 * unchanged; callers drive fetches explicitly via `fetchLicenses`.
 *
 * @param {ReturnType<import('react-oidc-context').useAuth>} auth
 */
export function useLicenseFetch(auth) {
  const [licenses, setLicenses] = useState(/** @type {License[]} */ ([]));
  const [nextToken, setNextToken] = useState(/** @type {string|null} */ (null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  const fetchLicenses = useCallback(
    async (term, size, deleted, token = null, reset = false, expiryStatus = 'all', customerNumber = '') => {
      if (!auth.user?.access_token) {
        setError('No access token found. Please log in.');
        return;
      }
      setLoading(true);
      setError(null);

      try {
        const params = {
          searchTerm: term,
          pageSize: size.toString(),
          includeDeleted: deleted.toString(),
          expiryStatus,
        };
        if (token) {
          params.nextToken = token;
        }
        if (customerNumber) {
          params.customerNumber = customerNumber;
        }

        const result = await licenseService.listLicenses(params, auth.user.access_token);

        setLicenses((prev) => (reset ? result.data || [] : [...prev, ...(result.data || [])]));
        setNextToken(result.nextToken || null);
      } catch (err) {
        setError(`Failed to fetch licenses: ${err.message}`);
        console.error('Fetch licenses error:', err);
      } finally {
        setLoading(false);
      }
    },
    [auth.user?.access_token],
  );

  const fetchLicenseExpiry = useCallback(
    async (serialNumber) => {
      if (!auth.user?.access_token) {
        setError('No access token found. Please log in.');
        return null;
      }

      try {
        return await licenseService.getLicenseExpiry(serialNumber, auth.user.access_token);
      } catch (err) {
        setError(`Failed to fetch license expiry: ${err.message}`);
        console.error('Fetch license expiry error:', err);
        return null;
      }
    },
    [auth.user?.access_token],
  );

  return {
    licenses,
    setLicenses,
    nextToken,
    loading,
    setLoading,
    error,
    setError,
    fetchLicenses,
    fetchLicenseExpiry,
  };
}
